import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { BaseSubsectionAdapter } from 'components/Assessments/adapters/BaseSubsectionAdapter'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { getAnswerValuesFromItems } from 'components/Assessments/helpers'
import { SocFormData } from 'components/Assessments/SocAssessment/types/SocFormData'
import { SocFormField } from 'components/Assessments/SocAssessment/types/SocFormField'
import { SocAssessmentQuestionnaireItem } from 'components/Assessments/SocAssessment/types/questionnaire'

/**
 * Therapies subsection adapter
 * Handles therapy services and home exercise plans (5 fields)
 */
export class TherapiesAdapter extends BaseSubsectionAdapter<SocFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<SocFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Direct field extractions
    const therapies = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_THERAPIES_THERAPIES
    )

    const therapyProvider = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_THERAPIES_THERAPY_PROVIDER
    )?.at(0)

    const homeExercisePlan = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_THERAPIES_HOME_EXERCISE_PLAN
    )?.at(0)

    const homeExerciseDuration = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_THERAPIES_HOME_EXERCISE_DURATION
    )?.at(0)

    const homeExerciseFrequency = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_THERAPIES_HOME_EXERCISE_FREQUENCY
    )?.at(0)

    return {
      [SocFormField.THERAPIES_THERAPIES]: therapies,
      [SocFormField.THERAPIES_THERAPY_PROVIDER]: therapyProvider,
      [SocFormField.THERAPIES_HOME_EXERCISE_PLAN]: homeExercisePlan,
      [SocFormField.THERAPIES_HOME_EXERCISE_DURATION]: homeExerciseDuration,
      [SocFormField.THERAPIES_HOME_EXERCISE_FREQUENCY]: homeExerciseFrequency
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = [
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_THERAPIES_THERAPIES,
        value: this.getFormDataField(SocFormField.THERAPIES_THERAPIES)
      }
    ]
    if (this.formDataFieldExists(SocFormField.THERAPIES_THERAPY_PROVIDER)) {
      mutations.push({
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_THERAPIES_THERAPY_PROVIDER,
        value: this.getFormDataField(SocFormField.THERAPIES_THERAPY_PROVIDER)
      })
    }
    if (this.formDataFieldExists(SocFormField.THERAPIES_HOME_EXERCISE_PLAN)) {
      mutations.push({
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_THERAPIES_HOME_EXERCISE_PLAN,
        value: this.getFormDataField(SocFormField.THERAPIES_HOME_EXERCISE_PLAN)
      })
    }

    if (this.formDataFieldExists(SocFormField.THERAPIES_HOME_EXERCISE_DURATION)) {
      mutations.push({
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_THERAPIES_HOME_EXERCISE_DURATION,
        value: this.getFormDataField(SocFormField.THERAPIES_HOME_EXERCISE_DURATION)
      })
    }
    if (this.formDataFieldExists(SocFormField.THERAPIES_HOME_EXERCISE_FREQUENCY)) {
      mutations.push({
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_THERAPIES_HOME_EXERCISE_FREQUENCY,
        value: this.getFormDataField(SocFormField.THERAPIES_HOME_EXERCISE_FREQUENCY)
      })
    }

    return mutations
  }
}
